import { useState, useEffect, useCallback } from "react";
import { Loader2, Plus, Trash2, Shield, UserCheck, UserX, Mail, CheckCircle, PauseCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const ROLE_LABELS = {
  titolare: { label: "Titolare", color: "bg-zinc-900 text-white" },
  agente: { label: "Agente", color: "bg-blue-100 text-blue-700" },
  segretaria: { label: "Segretaria", color: "bg-purple-100 text-purple-700" },
  assistente: { label: "Assistente", color: "bg-emerald-100 text-emerald-700" },
};

const SERVICE_LABELS = {
  property_radar: "Property Radar",
  lead_radar: "LeadRadar",
  app_siacasa: "App Siacasa",
  agenzie: "Monitoraggio Agenzie",
  zone: "Zone Agenti",
  portali: "Portali",
  impostazioni: "Impostazioni",
};

const ALL_SERVICES = [
  { id: "property_radar", label: "Property Radar" },
  { id: "lead_radar", label: "LeadRadar" },
  { id: "app_siacasa", label: "App Siacasa" },
  { id: "agenzie", label: "Monitoraggio Agenzie" },
  { id: "zone", label: "Zone Agenti" },
  { id: "portali", label: "Portali" },
  { id: "impostazioni", label: "Impostazioni" },
];

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "agente", services: [], sectors: ["residenziale"] });
  const [saving, setSaving] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const toggleService = (svcId) => {
    setForm(prev => ({
      ...prev,
      services: prev.services.includes(svcId)
        ? prev.services.filter(s => s !== svcId)
        : [...prev.services, svcId]
    }));
  };

  const addUser = async () => {
    if (!form.name || !form.email || !form.password) {
      toast.error("Compila tutti i campi");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/users`, form);
      setUsers(prev => [...prev, res.data]);
      setShowAdd(false);
      setForm({ name: "", email: "", password: "", role: "agente", services: [], sectors: ["residenziale"] });
      toast.success(`Utente ${res.data.name} creato`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
    setSaving(false);
  };

  const deleteUser = async (userId, userName) => {
    if (!window.confirm(`Eliminare definitivamente l'utente ${userName}? Questa azione è irreversibile.`)) return;
    try {
      await axios.delete(`${API}/users/${userId}`);
      setUsers(prev => prev.filter(u => u.id !== userId));
      toast.success("Utente eliminato");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const setActive = async (userId, active) => {
    try {
      const res = await axios.put(`${API}/auth/users/${userId}`, { is_active: active });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_active: res.data.is_active } : u));
      toast.success(active ? "Utente attivato" : "Utente sospeso");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const sendTestEmail = async (agentName) => {
    setSendingEmail(agentName);
    try {
      const res = await axios.post(`${API}/email/simulate/${agentName}`);
      if (res.data.disabled) {
        toast.warning(res.data.message);
      } else {
        toast.success(res.data.message);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore invio email");
    }
    setSendingEmail(null);
  };

  if (loading) {
    return <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div>;
  }

  return (
    <div data-testid="user-management-page" className="p-4 sm:p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-4xl font-black text-zinc-900 tracking-tighter">Gestione Utenti</h1>
          <p className="text-sm text-zinc-500 mt-0.5">{users.length} utenti configurati</p>
        </div>
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-btn" className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm h-9 px-4 text-xs font-semibold">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Nuovo Utente
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[420px]">
            <DialogHeader><DialogTitle className="font-heading text-lg font-bold">Nuovo Utente</DialogTitle></DialogHeader>
            <div className="space-y-3 pt-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-[10px] tracking-wider uppercase font-semibold text-zinc-500">Nome</Label>
                  <Input data-testid="user-name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Mario Rossi" className="h-9 rounded-sm border-zinc-300 text-sm" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] tracking-wider uppercase font-semibold text-zinc-500">Ruolo</Label>
                  <Select value={form.role} onValueChange={v => setForm(p => ({ ...p, role: v }))}>
                    <SelectTrigger className="h-9 rounded-sm border-zinc-300 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="agente">Agente</SelectItem>
                      <SelectItem value="assistente">Assistente</SelectItem>
                      <SelectItem value="segretaria">Segretaria</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] tracking-wider uppercase font-semibold text-zinc-500">Email</Label>
                <Input data-testid="user-email" type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="nome@siacasa.it" className="h-9 rounded-sm border-zinc-300 text-sm" />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] tracking-wider uppercase font-semibold text-zinc-500">Password</Label>
                <Input data-testid="user-password" type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} placeholder="••••••••" className="h-9 rounded-sm border-zinc-300 text-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] tracking-wider uppercase font-semibold text-zinc-500">Settore</Label>
                <div className="flex gap-2">
                  {[{id: "residenziale", label: "Residenziale"}, {id: "commerciale", label: "Commerciale"}].map(s => (
                    <button key={s.id} onClick={() => setForm(p => ({...p, sectors: p.sectors.includes(s.id) ? p.sectors.filter(x => x !== s.id) : [...p.sectors, s.id]}))}
                      className={`text-xs font-medium px-3 py-1.5 rounded-sm border transition-colors ${form.sectors.includes(s.id) ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-500 border-zinc-200 hover:border-zinc-400"}`}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] tracking-wider uppercase font-semibold text-zinc-500">Servizi accessibili</Label>
                <div className="flex flex-wrap gap-1.5">
                  {ALL_SERVICES.map(svc => (
                    <button
                      key={svc.id}
                      onClick={() => toggleService(svc.id)}
                      className={`text-xs font-medium px-2.5 py-1 rounded-sm border transition-colors ${
                        form.services.includes(svc.id)
                          ? "bg-zinc-900 text-white border-zinc-900"
                          : "bg-white text-zinc-500 border-zinc-200 hover:border-zinc-400"
                      }`}
                    >
                      {svc.label}
                    </button>
                  ))}
                </div>
              </div>
              <Button data-testid="confirm-add-user" onClick={addUser} disabled={saving} className="w-full bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm h-9 text-sm font-semibold">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crea Utente"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Users list */}
      <div className="space-y-2">
        {users.map(u => {
          const roleInfo = ROLE_LABELS[u.role] || ROLE_LABELS.agente;
          const userSvcs = u.role === "titolare" ? Object.keys(SERVICE_LABELS) : (u.services || []);
          const isActive = u.is_active !== false;
          const firstName = u.name?.split(" ")[0] || u.name;
          return (
            <div key={u.id} data-testid={`user-${u.id}`} className={`border rounded-sm p-4 transition-all ${isActive ? "border-zinc-200 bg-white" : "border-red-200 bg-red-50/20"}`}>
              {/* Top row: avatar + info + role */}
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-sm flex items-center justify-center shrink-0 ${isActive ? "bg-zinc-100" : "bg-red-100"}`}>
                  {u.role === "titolare" ? <Shield className="w-5 h-5 text-zinc-600" /> : isActive ? <UserCheck className="w-5 h-5 text-emerald-500" /> : <UserX className="w-5 h-5 text-red-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-zinc-900">{u.name}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-sm ${roleInfo.color}`}>{roleInfo.label}</span>
                    {!isActive && <span className="text-[9px] font-bold text-red-600 bg-red-100 px-1.5 py-0.5 rounded-sm border border-red-200">SOSPESO</span>}
                    {u.parent_agent && <span className="text-[9px] font-semibold text-violet-600 bg-violet-50 px-1.5 py-0.5 rounded-sm border border-violet-200">Sub-agente di {u.parent_agent}</span>}
                  </div>
                  <div className="text-[11px] text-zinc-400 mt-0.5">{u.email}</div>
                </div>
              </div>

              {/* Sectors + Services */}
              <div className="flex flex-wrap gap-1 mt-2.5 ml-[52px]">
                {(u.sectors || []).includes("residenziale") && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm bg-teal-50 text-teal-700 border border-teal-200">Residenziale</span>
                )}
                {(u.sectors || []).includes("commerciale") && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm bg-indigo-50 text-indigo-700 border border-indigo-200">Commerciale</span>
                )}
                {(!u.sectors || u.sectors.length === 0) && u.role !== "titolare" && (
                  <span className="text-[9px] text-zinc-400 italic">Nessun settore</span>
                )}
                {u.role === "titolare" && (
                  <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-sm bg-zinc-900 text-white">Tutti i settori</span>
                )}
                {userSvcs.length > 0 ? (
                  userSvcs.map(svcId => (
                    <span key={svcId} className="text-[9px] font-semibold px-1.5 py-0.5 rounded-sm bg-zinc-100 text-zinc-600 border border-zinc-200">
                      {SERVICE_LABELS[svcId] || svcId}
                    </span>
                  ))
                ) : (
                  <span className="text-[9px] text-zinc-400 italic">Nessun servizio</span>
                )}
              </div>

              {/* Actions row */}
              {u.role === "titolare" && (
                <div className="flex items-center gap-2 mt-3 ml-[52px]">
                  <button
                    data-testid={`email-toggle-${u.id}`}
                    onClick={async () => {
                      const newVal = !u.email_enabled;
                      try {
                        await axios.put(`${API}/auth/users/${u.id}`, { email_enabled: newVal });
                        setUsers(prev => prev.map(x => x.id === u.id ? {...x, email_enabled: newVal} : x));
                        toast.success(`Email ${newVal ? "abilitate" : "disabilitate"}`);
                      } catch { toast.error("Errore"); }
                    }}
                    className={`flex items-center gap-1.5 px-2 py-1 rounded-sm text-[10px] font-bold border transition-colors ${u.email_enabled ? "bg-blue-100 text-blue-700 border-blue-300" : "bg-zinc-50 text-zinc-400 border-zinc-200 hover:border-zinc-400"}`}
                  >
                    <Mail className="w-3 h-3" />
                    {u.email_enabled ? "Email abilitate" : "Email disabilitate"}
                  </button>
                </div>
              )}
              {u.role !== "titolare" && (
                <div className="flex items-center gap-2 mt-3 ml-[52px] flex-wrap">
                  {/* Sector toggles */}
                  {[{id: "residenziale", label: "Res.", color: "teal"}, {id: "commerciale", label: "Comm.", color: "indigo"}].map(s => {
                    const has = (u.sectors || []).includes(s.id);
                    return (
                      <button key={s.id} data-testid={`sector-${s.id}-${u.id}`}
                        onClick={async () => {
                          const newSectors = has ? (u.sectors || []).filter(x => x !== s.id) : [...(u.sectors || []), s.id];
                          try {
                            await axios.put(`${API}/auth/users/${u.id}`, { sectors: newSectors });
                            setUsers(prev => prev.map(x => x.id === u.id ? {...x, sectors: newSectors} : x));
                            toast.success(`Settore ${s.label} ${has ? "rimosso" : "aggiunto"}`);
                          } catch { toast.error("Errore"); }
                        }}
                        className={`text-[10px] font-bold px-2 py-1 rounded-sm border transition-colors ${has ? `bg-${s.color}-100 text-${s.color}-700 border-${s.color}-300` : "bg-zinc-50 text-zinc-400 border-zinc-200 hover:border-zinc-400"}`}
                      >
                        {has ? "✓" : "+"} {s.label}
                      </button>
                    );
                  })}
                  <div className="h-4 w-px bg-zinc-200" />
                  {/* Email toggle */}
                  <button
                    data-testid={`email-toggle-${u.id}`}
                    onClick={async () => {
                      const newVal = !u.email_enabled;
                      try {
                        await axios.put(`${API}/auth/users/${u.id}`, { email_enabled: newVal });
                        setUsers(prev => prev.map(x => x.id === u.id ? {...x, email_enabled: newVal} : x));
                        toast.success(`Email ${newVal ? "abilitate" : "disabilitate"} per ${u.name.split(" ")[0]}`);
                      } catch { toast.error("Errore"); }
                    }}
                    className={`flex items-center gap-1.5 px-2 py-1 rounded-sm text-[10px] font-bold border transition-colors ${u.email_enabled ? "bg-blue-100 text-blue-700 border-blue-300" : "bg-zinc-50 text-zinc-400 border-zinc-200 hover:border-zinc-400"}`}
                  >
                    <Mail className="w-3 h-3" />
                    {u.email_enabled ? "✓ Email" : "Email"}
                  </button>
                  <div className="h-4 w-px bg-zinc-200" />
                  {isActive ? (
                    <button
                      data-testid={`suspend-user-${u.id}`}
                      onClick={() => setActive(u.id, false)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors"
                    >
                      <PauseCircle className="w-3.5 h-3.5" />
                      Sospendi
                    </button>
                  ) : (
                    <button
                      data-testid={`activate-user-${u.id}`}
                      onClick={() => setActive(u.id, true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"
                    >
                      <CheckCircle className="w-3.5 h-3.5" />
                      Attiva
                    </button>
                  )}
                  <button
                    data-testid={`delete-user-${u.id}`}
                    onClick={() => deleteUser(u.id, u.name)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Rimuovi
                  </button>
                  {(u.role === "agente" || u.role === "assistente") && isActive && (
                    <button
                      data-testid={`email-user-${u.id}`}
                      onClick={() => sendTestEmail(firstName)}
                      disabled={sendingEmail === firstName}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors ml-auto"
                    >
                      {sendingEmail === firstName ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                      Invia Email Test
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Reception email test */}
      <div className="border border-dashed border-zinc-300 rounded-sm p-4 flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-zinc-700">Email Riepilogativa (Reception)</div>
          <div className="text-xs text-zinc-400">Invia il riepilogo giornaliero a info@siacasa.it</div>
        </div>
        <button
          data-testid="email-reception-btn"
          onClick={() => sendTestEmail("reception")}
          disabled={sendingEmail === "reception"}
          className="flex items-center gap-1.5 px-4 py-2 rounded-sm text-xs font-semibold border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
        >
          {sendingEmail === "reception" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
          Invia Email Reception
        </button>
      </div>
    </div>
  );
}
